import type { BlockMessage } from '@sunstone/api-client/hooks';
import { useChatService } from '@sunstone/api-client/hooks';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Textarea,
} from '@sunstone/ui';
import { Loader2, SendHorizontal, Settings, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useAuthToken } from '@/features/auth';
import { BlockRenderer } from '@/features/chat/blocks';

function MessageBubble({ message }: { message: BlockMessage }) {
  const isUser = message.role === 'user';
  const lastBlock = message.blocks[message.blocks.length - 1];
  const hasContent = message.blocks.some((b) => b.content.length > 0);

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[80%] rounded-lg px-4 py-3 text-sm ${
          isUser ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'
        }`}
      >
        {isUser ? (
          <div className="whitespace-pre-wrap">{message.blocks[0]?.content}</div>
        ) : (
          <>
            {message.blocks.map((block) => (
              <div key={block.id}>
                <BlockRenderer block={block} />
              </div>
            ))}

            {message.isStreaming && !hasContent && (
              <div className="flex gap-1 py-0.5">
                <span className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-current [animation-delay:-0.3s]" />
                <span className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-current [animation-delay:-0.15s]" />
                <span className="inline-block h-1.5 w-1.5 animate-bounce rounded-full bg-current" />
              </div>
            )}

            {message.isStreaming && hasContent && lastBlock?.status === 'streaming' && (
              <span className="inline-block h-3.5 w-0.5 animate-pulse bg-current ml-0.5 align-middle" />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export function ChatPage() {
  const { token, isLoading: tokenLoading, tokenError } = useAuthToken();
  const { messages, sendMessage, clearMessages, isStreaming, error } = useChatService(token);

  const [input, setInput] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [model, setModel] = useState('');
  const [showConfig, setShowConfig] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const handleSend = async () => {
    const content = input.trim();
    if (!content || isStreaming) return;
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
    await sendMessage(content, {
      systemPrompt: systemPrompt.trim() || undefined,
      model: model.trim() || undefined,
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = `${Math.min(e.target.scrollHeight, 200)}px`;
  };

  if (tokenLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (tokenError) {
    return (
      <div className="flex h-64 items-center justify-center p-4">
        <Alert variant="destructive" className="max-w-md">
          <AlertDescription>Authentication error: {tokenError.message}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col animate-in fade-in slide-in-from-bottom-4 duration-500"
      style={{ height: 'calc(100vh - 8rem)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4 shrink-0">
        <h1 className="text-3xl font-bold tracking-tight">Chat</h1>
        <div className="flex items-center gap-2">
          <Button
            variant={showConfig ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setShowConfig(!showConfig)}
          >
            <Settings />
            Configure
          </Button>
          {messages.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearMessages}
              disabled={isStreaming}
              className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 />
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Configuration panel */}
      {showConfig && (
        <Card className="mb-4 shrink-0">
          <CardContent className="pt-4 space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="chat-system-prompt" className="text-xs uppercase tracking-wide">
                System Prompt
              </Label>
              <Textarea
                id="chat-system-prompt"
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                placeholder="You are a helpful assistant..."
                rows={3}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="chat-model-override" className="text-xs uppercase tracking-wide">
                Model Override
              </Label>
              <Input
                id="chat-model-override"
                type="text"
                value={model}
                onChange={(e) => setModel(e.target.value)}
                placeholder="Leave blank for default model"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-4 pb-4 min-h-0">
        {messages.length === 0 && (
          <div className="flex h-full items-center justify-center">
            <div className="text-center text-muted-foreground">
              <p className="text-lg font-medium mb-1">Start a conversation</p>
              <p className="text-sm">Send a message to interact with the LLM</p>
            </div>
          </div>
        )}

        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} />
        ))}

        <div ref={messagesEndRef} />
      </div>

      {/* Error banner */}
      {error && (
        <Alert variant="destructive" className="mb-2 shrink-0">
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      )}

      {/* Input */}
      <Card className="shrink-0">
        <CardContent className="flex items-end gap-3 pt-4">
          <Textarea
            ref={textareaRef}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Type a message... (Enter to send, Shift+Enter for newline)"
            disabled={isStreaming}
            rows={1}
            className="flex-1 resize-none border-0 shadow-none focus-visible:ring-0 focus-visible:border-0 min-h-0 p-0 text-sm"
            style={{ maxHeight: '200px' }}
          />
          <Button
            onClick={handleSend}
            disabled={!input.trim() || isStreaming}
            size="icon"
            className="rounded-full shrink-0"
          >
            {isStreaming ? <Loader2 className="animate-spin" /> : <SendHorizontal />}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
